import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { TYPE_DATE_PICKER } from 'utils/constants/sportsbook/results';
import { showToastError } from 'utils/generic/show-toast-error';
import { ResultsGame } from 'interfaces/results';
import { getResultGames } from 'services/sportsbook/results';
import { usePrevious } from 'hooks/usePrevious';
import { useSearchParams } from 'hooks/useSearchParams';
import { setSport } from 'store/actions/results-data';
import {
  getResultsCompetition,
  getResultsDataLoading,
  getResultsDate,
  getResultsSport,
  getResultsStatus,
  getResultsTeam,
  getResultsTrigger
} from 'store/selectors/results-data';

type Props = {
  games: ResultsGame[] | null;
  resultsSportType: string | undefined | null;
  setGames: Dispatch<SetStateAction<ResultsGame[] | null>>;
};

export const useGetResultGames = ({
  setGames,
  games,
  resultsSportType
}: Props): boolean => {
  const dispatch = useDispatch();
  const { game_id } = useSearchParams();
  const prevGameId = usePrevious(game_id);
  const loadingRef = useRef(false);
  const resultsTeam = useSelector(getResultsTeam);
  const resultsDate = useSelector(getResultsDate);
  const resultsSport = useSelector(getResultsSport);
  const resultsStatus = useSelector(getResultsStatus);
  const resultsTrigger = useSelector(getResultsTrigger);
  const resultsDataLoading = useSelector(getResultsDataLoading);
  const resultsCompetition = useSelector(getResultsCompetition);
  const prevResultsDataLoadingRef = usePrevious(resultsDataLoading);
  const currentDate = useRef<number | undefined>(undefined);

  const isGameIdChanged = useMemo(() => {
    return game_id !== prevGameId;
  }, [game_id, prevGameId]);

  const isNeedToFetchGames = useMemo(
    () =>
      !games ||
      (isGameIdChanged &&
        ((game_id && !games.some(game => game.game_id !== game_id)) ||
          (!game_id && games?.length === 1))),
    [isGameIdChanged, game_id, games]
  );

  const getGames = () => {
    if (resultsDataLoading || loadingRef.current) {
      return;
    }

    if (resultsSportType && !resultsSport) {
      return;
    }

    if (!games?.some(game => game.game_id === game_id)) {
      loadingRef.current = true;
      setGames(null);
    }

    const now = resultsDate.to;

    const params = game_id
      ? { game_id }
      : {
          live: resultsStatus === 'live',
          sport_id: resultsSport || 1,
          competition_id: resultsCompetition,
          team_id: resultsTeam,
          sport_type: resultsSportType || null,
          from_date: dayjs(resultsDate[TYPE_DATE_PICKER ? 'from' : 'to'])
            .startOf('day')
            .unix(),
          to_date: dayjs(resultsDate.to).endOf('day').unix()
        };

    currentDate.current = resultsDate.to;
    getResultGames(
      params,
      (data: {
        games?: { game: ResultsGame[] };
        status?: number;
        details?: string;
      }) => {
        if (now !== currentDate.current) {
          return;
        }

        if (data.status) {
          showToastError(data.details || 'Unknown error');
        }

        if (game_id && Number(data.games?.game?.[0]?.sport_id)) {
          dispatch(setSport(Number(data.games?.game[0].sport_id)));
        }

        setGames(data.games?.game?.sort((a, b) => a.date - b.date) || []);
        loadingRef.current = false;
      }
    );
  };

  useEffect(() => {
    if (isNeedToFetchGames) {
      getGames();
    }
  }, [isNeedToFetchGames, isGameIdChanged, game_id]);

  useLayoutEffect(() => {
    if (
      resultsTrigger !== '' &&
      !resultsDataLoading &&
      prevResultsDataLoadingRef
    ) {
      getGames();
    }
  }, [resultsTrigger, resultsDataLoading]);

  return loadingRef.current;
};
